<template>
  <div class="px-6">
    <Loader v-if="loading" />
    <v-form v-else ref="form" v-model="valid">
      <v-row>
        <!-- Ramka 1 - Dane firmy -->
        <v-col cols="12" md="6">
          <v-card outlined class="pa-4">
            <h5 class="mb-4">{{ $t('tslContractors.companyData') }}</h5>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="contractor.companyName"
                  :label="$t('tslContractors.companyName')"
                  :rules="[rules.required]"
                  :readonly="!editMode"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="contractor.vatNumber"
                  :label="$t('tslContractors.vatNumber')"
                  :rules="[rules.required]"
                  :readonly="!editMode"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="6">
                <CountryFlagSelect
                  v-model="contractor.country"
                  :label="$t('tslContractors.country')"
                  :rules="[rules.required]"
                  :disabled="!editMode"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="contractor.street"
                  :label="$t('tslContractors.street')"
                  :rules="[rules.required]"
                  :readonly="!editMode"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="contractor.city"
                  :label="$t('tslContractors.city')"
                  :rules="[rules.required]"
                  :readonly="!editMode"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <!-- Ramka 2 - Dane kontaktowe -->
        <v-col cols="12" md="6">
          <v-card outlined class="pa-4">
            <h5 class="mb-4">{{ $t('tslContractors.contactData') }}</h5>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="contractor.firstName"
                  :label="$t('tslContractors.firstName')"
                  :readonly="!editMode"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="contractor.lastName"
                  :label="$t('tslContractors.lastName')"
                  :readonly="!editMode"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="contractor.phone"
                  :label="$t('tslContractors.phone')"
                  :readonly="!editMode"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="contractor.email"
                  :label="$t('tslContractors.email')"
                  :readonly="!editMode"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col v-if="contractor.type === 1" cols="12">
                <v-textarea
                  v-model="contractor.invoiceData"
                  :label="$t('tslContractors.invoiceData')"
                  :readonly="!editMode"
                  outlined
                  dense
                  rows="2"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="contractor.notes"
                  :label="$t('tslContractors.notes')"
                  :readonly="!editMode"
                  outlined
                  dense
                  rows="2"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="contractor.cooperationStatus"
                  :items="cooperationStatuses"
                  item-text="text"
                  item-value="value"
                  :label="$t('tslContractors.cooperationStatus')"
                  :readonly="!editMode || !canEditStatus"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <template v-if="contractor.type === 2">
                <v-col cols="6"></v-col>
                <v-col cols="6">
                  <v-select
                    v-model="contractor.paymentType"
                    :items="paymentTypes"
                    item-text="text"
                    item-value="value"
                    :label="$t('tslContractors.paymentType')"
                    :readonly="!editMode"
                    outlined
                    dense
                    hide-details="auto"
                  />
                </v-col>
                <v-col v-if="contractor.paymentType === 1" cols="6">
                  <v-text-field
                    v-model.number="contractor.deferredPaymentDays"
                    :label="$t('tslContractors.deferredPaymentDays')"
                    :readonly="!editMode"
                    type="number"
                    outlined
                    dense
                    hide-details="auto"
                  />
                </v-col>
              </template>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12" class="text-right">
          <v-btn v-if="!editMode" color="primary" @click="startEdit" depressed>
            {{ $t('tslContractors.edit') }}
          </v-btn>
          <template v-else>
            <v-btn text @click="cancelEdit" class="mr-2">
              {{ $t('tslContractors.cancel') }}
            </v-btn>
            <v-btn color="primary" :disabled="!valid" @click="save" depressed>
              {{ $t('tslContractors.save') }}
            </v-btn>
          </template>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import Service from '@/services'
import Loader from '@/components/Loader'
import CountryFlagSelect from '@/components/CountryFlagSelect'

export default {
  name: 'Information',
  components: {
    Loader,
    CountryFlagSelect
  },
  props: {
    contractorId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      editMode: false,
      valid: false,
      contractor: {},
      originalContractor: {},
      cooperationStatuses: [
        { text: this.$t('tslContractors.active'), value: 1 },
        { text: this.$t('tslContractors.suspended'), value: 2 }
      ],
      paymentTypes: [
        { text: this.$t('dateOfPayment.none'), value: 0 },
        { text: this.$t('dateOfPayment.deferred'), value: 1 },
        { text: this.$t('dateOfPayment.paymentOnUnloading'), value: 2 },
        { text: this.$t('dateOfPayment.paymentInAdvance'), value: 3 }
      ],
      rules: {
        required: value => !!value || this.$t('tslContractors.validationRequired')
      }
    }
  },
  computed: {
    canEditStatus () {
      return this.$store.getters.isOwner || this.$store.getters.isManager
    }
  },
  created () {
    this.loadContractor()
  },
  methods: {
    async loadContractor () {
      this.loading = true
      try {
        const { data } = await Service.get('/TSLContractor/GetContractorDetails', { id: this.contractorId })
        this.contractor = { ...data }
        this.originalContractor = { ...data }
      } catch (e) {
        this.$store.dispatch('setErrorNotification', e.response?.data || 'Error')
      } finally {
        this.loading = false
      }
    },
    startEdit () {
      this.editMode = true
    },
    cancelEdit () {
      this.contractor = { ...this.originalContractor }
      this.editMode = false
    },
    async save () {
      if (!this.$refs.form.validate()) return
      try {
        await Service.put('/TSLContractor/UpdateContractor', {
          contractor: this.contractor
        })
        this.$store.dispatch('setSuccessNotification', this.$t('tslContractors.updated'))
        this.editMode = false
        this.$emit('updated')
        await this.loadContractor()
      } catch (e) {
        this.$store.dispatch('setErrorNotification', e.response?.data || 'Error')
      }
    }
  }
}
</script>
